import { useAppSelector } from '@shared/redux';
import { authSelectors } from '@shared/redux/ducks/auth';
import { socket, type SocketClient } from '@shared/services/socket';
import { setEventsHandlers } from '@shared/services/socket/handlers';
import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from 'react';

interface SocketContextValue {
  socket: SocketClient;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextValue | null>(null);

export const SocketProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isConnected, dispatchConnectionState] = useReducer(
    (_: boolean, next: boolean) => next,
    false,
  );
  const accessToken = useAppSelector(authSelectors.selectAccessToken) ?? null;
  const handleSocketOpen = useCallback(() => dispatchConnectionState(true), []);
  const handleSocketClose = useCallback(() => dispatchConnectionState(false), []);
  const contextValue = useMemo(() => ({ socket, isConnected }), [isConnected]);

  useEffect(() => {
    if (accessToken === null) {
      dispatchConnectionState(false);
      socket.disconnect();
      return;
    }

    const unsubscribeSocketEvents = [
      socket.onOpen(handleSocketOpen),
      socket.onClose(handleSocketClose),
    ];
    const unsubscribeEventHandlers = setEventsHandlers();
    let isDisposed = false;
    let stopLocalChatEventsSimulator: (() => void) | undefined;

    if (import.meta.env.DEV) {
      void import('@shared/services/socket/dev-chat-events-simulator').then(
        ({ setupLocalChatEventsSimulator }) => {
          if (isDisposed) return;
          stopLocalChatEventsSimulator = setupLocalChatEventsSimulator();
        },
      );
    }

    dispatchConnectionState(socket.readyState === WebSocket.OPEN);
    void socket.connect();

    return () => {
      isDisposed = true;
      stopLocalChatEventsSimulator?.();
      unsubscribeSocketEvents.forEach((unsubscribe) => unsubscribe());
      unsubscribeEventHandlers();
    };
  }, [accessToken, handleSocketClose, handleSocketOpen]);

  return <SocketContext.Provider value={contextValue}>{children}</SocketContext.Provider>;
};
